import React from 'react';
import { useGetIdentity } from '@refinedev/core';
import { User } from '../interfaces/user';

interface PermissionGuardProps {
  children: React.ReactNode;
  requiredRoles: string[];
  fallback?: React.ReactNode;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  requiredRoles,
  fallback = null,
}) => {
  const { data: user } = useGetIdentity<User>();

  // 如果沒有用戶資料，顯示 fallback
  if (!user) {
    return <>{fallback}</>;
  }

  // 檢查用戶角色是否符合要求
  if (!requiredRoles.includes(user.role)) {
    return <>{fallback}</>;
  }

  // 權限檢查通過，顯示子組件
  return <>{children}</>;
};

// 預設的無權限顯示組件
export const NoPermissionFallback: React.FC = () => (
  <div className="min-h-[400px] flex items-center justify-center">
    <div className="text-center">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
        <svg 
          className="h-6 w-6 text-red-600" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" 
          />
        </svg>
      </div>
      <h3 className="mt-2 text-sm font-medium text-gray-900">權限不足</h3>
      <p className="mt-1 text-sm text-gray-500">
        您沒有權限存取此頁面，請聯繫管理員獲取相關權限。
      </p>
    </div>
  </div>
);

// 便利的 Hook 用於檢查權限
export const usePermission = () => {
  const { data: user } = useGetIdentity<User>();

  const hasRole = (roles: string | string[]): boolean => {
    if (!user) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(user.role);
  };

  const isAdmin = (): boolean => hasRole('admin');
  const isMaintainer = (): boolean => hasRole(['admin', 'maintainer']);
  const isUser = (): boolean => hasRole(['admin', 'maintainer', 'user']);

  return {
    user,
    hasRole,
    isAdmin,
    isMaintainer,
    isUser,
  };
};